import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Divider, Paper, Step, StepLabel, Stepper } from '@material-ui/core';
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import clsx from "clsx";
import ArrowForward from '@material-ui/icons/ArrowForwardOutlined';
import ArrowBack from '@material-ui/icons/ArrowBackOutlined';
import DescriptionOutlined from '@material-ui/icons/DescriptionOutlined';
import Save from '@material-ui/icons/SaveOutlined';
import { useNavigate, useLocation } from "react-router-dom";
import useErrorMessage from "../../../../hooks/useErrorMessage";
import useSuccessMessage from "../../../../hooks/useSuccessMessage";
import useAlertMessage from "../../../../hooks/useAlertMessage";
import useConfirmationDialog from "../../../shared/confirmationdialog/useConfirmationDialog";
import useCreate from "../../../../hooks/useCreate";
import { URL_INSCRICAO_SALVAR, URL_INSCRICAO_ATUALIZAR, URL_INSCRICAO_CREDENCIAL } from "../../../../constants";
import { containedButton, textButton } from "../../../../utils/styles";
import { mensagemDoErro } from "../../../../utils/erro";

import FormDadosPessoais from "./FormDadosPessoais";
import FormSocioEconomicos from "./FormSocioEconomicos";
import FormDadosInscricao from "./FormDadosInscricao";
import FormDocumentos from "./FormDocumentos";
import FormFinalizar from "./FormFinalizar";
import BoxResumoInscricao from "./BoxResumoInscricao";
import FloatingBottomBar from "../../../shared/floatingbottombar/FloatingBottomBar";
import MobileRegistrationNavigation from "./MobileRegistrationNavigation";
import { validarComSchema } from "./validacaoYup";
import useIsMobile from "../../../../hooks/useIsMobile";
import { getSchemaEtapa } from "./inscricaoSchemas";
import { buildMobileSteps, getStepSections, mensagemErroEtapa, pickSectionErrors, sectionHasError, STEP_LABELS, STEPS } from "./inscricaoSections";
import { montarPayload } from "./inscricaoPayload";
import {
    alteracoesDescartadasPeloPeriodo, etapaSomenteConsulta, AVISO_ALTERACOES_DESCARTADAS
} from "./periodoInscricao";

/* Intervalo entre renovações periódicas da credencial da área pública durante o preenchimento. */
const INTERVALO_RENOVACAO_CREDENCIAL = 10 * 60 * 1000;

const useStyles = makeStyles(theme => ({
    root: {
        [theme.breakpoints.down('sm')]: {
            paddingBottom: `calc(${theme.spacing(14)}px + env(safe-area-inset-bottom))`
        }
    },
    stepperRoot: {
        background: 'none',
        border: 'none',
        paddingTop: theme.spacing(),
        paddingRight: 0,
        paddingLeft: 0,
        [theme.breakpoints.up('sm')]: {
            paddingTop: theme.spacing(3),
            paddingRight: theme.spacing(3),
            paddingLeft: theme.spacing(3)
        }
    },
    contentDiv: {
        marginBottom: theme.spacing(2)
    },
    buttonsGrid: {
        flexDirection: "column-reverse",
        marginTop: theme.spacing(2),
        [theme.breakpoints.up('sm')]: {
            flexDirection: "initial"
        }
    },
    buttonsDiv: {
        textAlign: 'center',
        [theme.breakpoints.up('sm')]: {
            textAlign: 'right'
        }
    },
    voltarButton: {
        marginLeft: theme.spacing()
    },
    ...containedButton(theme, true, false),
    ...textButton(theme, false),
    containerPaper: {
        padding: theme.spacing(3),
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(2)
        }
    }
}));

export default function FormInscricao({ inscricao, onUpdate }) {
    const classes = useStyles();
    const childFormRef = useRef();
    const showErrorMessage = useErrorMessage();
    const showSuccessMessage = useSuccessMessage();
    const showAlertMessage = useAlertMessage();
    const showConfirmation = useConfirmationDialog();
    const navigate = useNavigate();
    const location = useLocation();
    const create = useCreate();

    /*
     * A credencial vence em 60 min sem uso e só as chamadas autenticadas a
     * renovam. Renova assim que monta e depois a cada 10 min enquanto este
     * formulário estiver aberto, para não vencer no meio do preenchimento —
     * seja na edição no desktop (sem troca de etapa) ou numa etapa única e
     * longa — e o candidato perder o que digitou e os PDFs já escolhidos.
     * Sem modal de carregamento e sem mensagem: é só manutenção; um 401 de
     * verdade continua tratado globalmente pelo async.js.
     */
    const [renovarCredencial] = useCreate({ returnInnerLoadingState: true });
    /*
     * useCreate captura a credencial do Redux no momento do render; a ref
     * sempre aponta para a versão mais recente, para o setInterval (que só
     * existe uma vez, montado no primeiro render) nunca chamar uma closure
     * com a credencial velha.
     */
    const renovarRef = useRef(renovarCredencial);
    renovarRef.current = renovarCredencial;

    const isEdit = location.state?.isEdit || false;
    const initialStep = location.state?.initialStep || 0;
    const identificacao = location.state?.identificacao;
    const formulario = {
        programaId: identificacao?.programaId ?? inscricao?.programaId ?? null,
        configuracoesFormulario: identificacao?.configuracoesFormulario ?? inscricao?.configuracoesFormulario ?? [],
        inscricoesProcessoSeletivo: identificacao?.inscricoesProcessoSeletivo ?? inscricao?.inscricoesProcessoSeletivo ?? [],
        categoriasInscricao: identificacao?.categoriasInscricao ?? inscricao?.categoriasInscricao ?? []
    };

    const isMobile = useIsMobile();
    const [activeStep, setActiveStep] = useState(initialStep);
    const [activeSectionId, setActiveSectionId] = useState(null);
    /* Envio ao servidor em andamento: segura Salvar/Finalizar contra clique duplo. */
    const [enviando, setEnviando] = useState(false);

    /*
     * Fora do período de inscrição (ou sem calendário de inscrição) a etapa
     * abre só para consulta — menos Documentos com um calendário de entrega
     * aberto —, com os campos travados e o Salvar desabilitado (o aviso do
     * período fica só na tela da inscrição). É a regra do back, que nesse
     * caso grava só os documentos e ignora o resto do payload.
     */
    const somenteConsulta = isEdit && etapaSomenteConsulta(activeStep, identificacao);

    /*
     * No mobile cada seção (antiga tab) é uma etapa própria. Em modo edição a lista
     * fica restrita às seções da etapa macro que o candidato escolheu editar.
     */
    const mobileSteps = useMemo(
        () => buildMobileSteps(
            { formulario, inscricao },
            { restrictToStepIndex: isEdit ? initialStep : null }
        ),
        [inscricao, formulario.programaId, formulario.configuracoesFormulario, isEdit, initialStep]
    );

    const mobileIndex = useMemo(() => {
        const exact = mobileSteps.findIndex(item => item.stepIndex === activeStep && item.sectionId === activeSectionId);
        if (exact !== -1) return exact;
        const firstOfStep = mobileSteps.findIndex(item => item.stepIndex === activeStep);
        return firstOfStep === -1 ? 0 : firstOfStep;
    }, [mobileSteps, activeStep, activeSectionId]);

    const currentMobileStep = mobileSteps[mobileIndex];

    /*
     * Etapa mais avançada já alcançada: o menu libera tudo até ela. Em edição a
     * inscrição já existe, então todas as seções da etapa saem liberadas.
     * O controle é por índice (e não por lista de visitadas) para que uma seção
     * que aparece no meio do caminho — Responsável Legal, quando o candidato
     * informa ser menor de idade — também fique acessível.
     */
    const [maxIndexAlcancado, setMaxIndexAlcancado] = useState(0);
    const maxLiberado = isEdit
        ? Math.max(mobileSteps.length - 1, 0)
        : Math.min(Math.max(maxIndexAlcancado, mobileIndex), Math.max(mobileSteps.length - 1, 0));

    /*
     * No mobile, depois de exibir os erros, leva a tela até o primeiro campo em
     * vermelho (a seção pode ser longa e o erro ficar fora da vista). Espera a
     * renderização com os erros antes de procurar o campo.
     */
    const conteudoRef = useRef(null);
    const exibirErrosDaEtapa = () => {
        const exibicao = childFormRef.current?.formulario?.exibirErros();
        if (!isMobile) return;

        Promise.resolve(exibicao).then(() => {
            setTimeout(() => {
                const primeiroErro = conteudoRef.current?.querySelector('.Mui-error');
                if (primeiroErro) {
                    primeiroErro.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
        });
    };

    /* Quando a navegação para numa etapa pendente, ela precisa já chegar mostrando os erros. */
    const submeterAoEntrarRef = useRef(false);

    useEffect(() => {
        if (!submeterAoEntrarRef.current) return;

        submeterAoEntrarRef.current = false;
        exibirErrosDaEtapa();
    }, [activeStep, activeSectionId]);

    useEffect(() => {
        renovarRef.current(URL_INSCRICAO_CREDENCIAL, {}, null, () => {});

        const intervalId = setInterval(() => {
            renovarRef.current(URL_INSCRICAO_CREDENCIAL, {}, null, () => {});
        }, INTERVALO_RENOVACAO_CREDENCIAL);

        return () => clearInterval(intervalId);
    }, []);

    const getStepContent = (step, sectionId) => {
        switch (step) {
            case 0:
                return <FormDadosPessoais ref={childFormRef} data={inscricao} onUpdate={onUpdate} isEdit={isEdit} identificacao={identificacao} formulario={formulario} activeSectionId={sectionId} somenteConsulta={somenteConsulta} />;
            case 1:
                return <FormSocioEconomicos ref={childFormRef} data={inscricao} onUpdate={onUpdate} formulario={formulario} activeSectionId={sectionId} somenteConsulta={somenteConsulta} />;
            case 2:
                return <FormDadosInscricao ref={childFormRef} data={inscricao} onUpdate={onUpdate} formulario={formulario} activeSectionId={sectionId} somenteConsulta={somenteConsulta} />;
            case 3:
                return <FormDocumentos
                    ref={childFormRef}
                    data={inscricao}
                    onUpdate={onUpdate}
                    focusPendingTab={location.state?.focusPendingTab}
                />;
            case 4:
                return <FormFinalizar ref={childFormRef} data={inscricao} onUpdate={onUpdate} />;
            default:
                return <></>;
        }
    };

    const validateAndNext = useCallback(() => {
        const etapa = childFormRef.current?.formulario;
        if (etapa) {
            etapa.validar().then((errors) => {
                if (Object.keys(errors).length === 0) {
                    onUpdate(etapa.getValores());
                    setActiveStep((prev) => prev + 1);
                } else {
                    exibirErrosDaEtapa();
                    const comErro = getStepSections(STEPS[activeStep].id, { formulario, inscricao })
                        .filter(secao => sectionHasError(secao, errors));
                    showErrorMessage(mensagemErroEtapa(comErro, errors, etapa.getValores()));
                }
            });
        } else {
            setActiveStep((prev) => prev + 1);
        }
    }, [onUpdate, showErrorMessage, activeStep, inscricao, formulario.programaId, formulario.configuracoesFormulario]);

    /* Move para uma posição da lista plana de etapas do mobile, sem validar. */
    const goToMobileIndex = (nextIndex, { mostrarErros = false } = {}) => {
        const target = mobileSteps[nextIndex];
        if (!target) return;

        if (childFormRef.current?.formulario) onUpdate(childFormRef.current.formulario.getValores());
        submeterAoEntrarRef.current = mostrarErros;
        setMaxIndexAlcancado(anterior => Math.max(anterior, nextIndex));
        setActiveStep(target.stepIndex);
        setActiveSectionId(target.sectionId);
        // Chegando para mostrar erros, quem posiciona a tela é o exibirErrosDaEtapa.
        if (!mostrarErros) window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    /* Valores acumulados da inscrição somados ao que está digitado na seção atual. */
    const valoresAtuais = () => ({ ...inscricao, ...(childFormRef.current?.formulario?.getValores() || {}) });

    /* Erros de uma etapa macro que pode nem estar montada. */
    const validarEtapa = async (stepId, valores) => {
        return validarComSchema(getSchemaEtapa(stepId, { formulario, inscricao: valores }), valores);
    };

    /*
     * Primeira seção pendente no caminho [de, ate) — é o que trava o pulo direto
     * para uma etapa mais à frente. Cada etapa macro é validada uma única vez.
     */
    const primeiraPendencia = async (de, ate, valores) => {
        const errosPorEtapa = new Map();

        for (let indice = de; indice < ate; indice++) {
            const passo = mobileSteps[indice];
            if (!passo) continue;

            if (!errosPorEtapa.has(passo.stepId)) {
                errosPorEtapa.set(passo.stepId, await validarEtapa(passo.stepId, valores));
            }

            const erros = errosPorEtapa.get(passo.stepId);
            if (Object.keys(pickSectionErrors(passo, erros)).length > 0) {
                return { indice, passo, erros };
            }
        }

        return null;
    };

    /*
     * Seleção de etapa pelo menu do mobile. Voltar é livre; avançar só chega ao
     * destino se tudo do caminho estiver preenchido, senão para na pendência.
     * Na consulta avançar também é livre: não há o que corrigir.
     */
    const onSelecionarEtapa = (destino) => {
        if (destino === mobileIndex || destino > maxLiberado) return;

        if (destino < mobileIndex || somenteConsulta) {
            goToMobileIndex(destino);
            return;
        }

        const valores = valoresAtuais();
        primeiraPendencia(mobileIndex, destino, valores).then((pendencia) => {
            if (!pendencia) {
                goToMobileIndex(destino);
                return;
            }

            showErrorMessage(mensagemErroEtapa([pendencia.passo], pendencia.erros, valores));

            if (pendencia.indice === mobileIndex) {
                exibirErrosDaEtapa();
                return;
            }

            goToMobileIndex(pendencia.indice, { mostrarErros: true });
        });
    };

    /*
     * No mobile o Avançar valida somente os campos da seção atual. Na consulta
     * não valida: dado antigo incompleto travaria a navegação sem ter como
     * corrigir.
     */
    const validateSectionAndNext = () => {
        const etapa = childFormRef.current?.formulario;
        if (!etapa || somenteConsulta) {
            goToMobileIndex(mobileIndex + 1);
            return;
        }

        etapa.validar().then((errors) => {
            if (Object.keys(pickSectionErrors(currentMobileStep, errors)).length === 0) {
                goToMobileIndex(mobileIndex + 1);
            } else {
                exibirErrosDaEtapa();
                showErrorMessage(mensagemErroEtapa([currentMobileStep], errors, etapa.getValores()));
            }
        });
    };

    const onNext = () => {
        if (isMobile) {
            validateSectionAndNext();
            return;
        }
        validateAndNext();
    };

    const onBack = () => {
        if (isMobile) {
            goToMobileIndex(mobileIndex - 1);
            return;
        }
        if (childFormRef.current?.formulario) {
            onUpdate(childFormRef.current.formulario.getValores());
        }
        setActiveStep((prev) => prev - 1);
    };

    const onCancel = () => {
        const sair = () => {
            if (isEdit && identificacao) {
                navigate('/publico/inscricao-existente', { state: { identificacao } });
            } else {
                navigate('/publico/identificacao');
            }
        };

        /* Na consulta não há alteração a perder: sai sem perguntar. */
        if (somenteConsulta) {
            sair();
            return;
        }

        showConfirmation(
            isEdit
                ? 'Deseja realmente cancelar a edição? As alterações não salvas serão perdidas.'
                : 'Tem certeza que deseja cancelar? Todos os dados preenchidos até agora serão perdidos.',
            sair
        );
    };

    /*
     * Resposta mínima (parcial): o back salvou mas não conseguiu reler a
     * inscrição, e devolveu só { inscrito, dadosCadastrais: { id, pessoa: { id } } }.
     * Nunca pode ser tratada como inscrição completa — editar a partir dela
     * mandaria as listas (documentos, grupo familiar etc.) vazias no próximo
     * salvar — então avisa e manda o candidato se identificar de novo.
     */
    const navegarAposSalvar = (response, mensagemSucesso) => {
        if (response?.parcial === true) {
            showSuccessMessage(`${mensagemSucesso} Para continuar, identifique-se novamente.`);
            navigate('/publico/identificacao');
            return;
        }
        /*
         * O período de inscrição fechou entre abrir a etapa e salvar: o back
         * gravou só os documentos e ignorou o resto, sem erro. Avisa em vez de
         * dar sucesso.
         */
        if (isEdit && alteracoesDescartadasPeloPeriodo(response, activeStep)) {
            showAlertMessage(AVISO_ALTERACOES_DESCARTADAS);
        } else {
            showSuccessMessage(mensagemSucesso);
        }
        navigate('/publico/inscricao-existente', { state: { identificacao: response } });
    };

    const onSaveEdit = () => {
        if (somenteConsulta) return;

        const etapa = childFormRef.current?.formulario;
        if (etapa) {
            etapa.validar().then((errors) => {
                if (Object.keys(errors).length === 0) {
                    showConfirmation(
                        'Deseja realmente salvar as alterações realizadas?',
                        () => {
                            const finalValues = { ...inscricao, ...etapa.getValores() };
                            onUpdate(finalValues);
                            const payload = montarPayload(finalValues, { identificacao, formulario });

                            setEnviando(true);
                            create(URL_INSCRICAO_ATUALIZAR, payload, (response) => {
                                navegarAposSalvar(response, 'Inscrição atualizada com sucesso!');
                            }, (error) => {
                                setEnviando(false);
                                showErrorMessage(mensagemDoErro(error, 'Falha ao salvar as alterações.'));
                            });
                        }
                    );
                } else {
                    exibirErrosDaEtapa();
                    const comErro = getStepSections(STEPS[activeStep].id, { formulario, inscricao })
                        .filter(secao => sectionHasError(secao, errors));
                    showErrorMessage(mensagemErroEtapa(comErro, errors, etapa.getValores()));
                }
            });
        }
    };

    const onFinish = () => {
        // Envio final
        const etapa = childFormRef.current?.formulario;
        if (etapa) {
            etapa.validar().then((errors) => {
                if (Object.keys(errors).length === 0) {
                    const finalValues = { ...inscricao, ...etapa.getValores() };
                    onUpdate(finalValues);
                    const payload = montarPayload(finalValues, { identificacao, formulario });

                    setEnviando(true);
                    create(isEdit ? URL_INSCRICAO_ATUALIZAR : URL_INSCRICAO_SALVAR, payload, (response) => {
                        navegarAposSalvar(response, isEdit ? 'Inscrição atualizada com sucesso!' : 'Inscrição realizada com sucesso!');
                    }, (error) => {
                        setEnviando(false);
                        showErrorMessage(mensagemDoErro(error, 'Falha ao concluir a inscrição.'));
                    });
                } else {
                    exibirErrosDaEtapa();
                    const comErro = getStepSections(STEPS[activeStep].id, { formulario, inscricao })
                        .filter(secao => sectionHasError(secao, errors));
                    showErrorMessage(mensagemErroEtapa(comErro, errors, etapa.getValores()));
                }
            });
        }
    };

    return (
        <div className={classes.root}>
            <Grid container spacing={3}>
                {!isEdit && !isMobile && (
                    <>
                        <Grid item xs={12} sm={12}>
                            <Stepper activeStep={activeStep} alternativeLabel classes={{
                                root: classes.stepperRoot
                            }}>
                                {STEP_LABELS.map((label) => (
                                    <Step key={label}>
                                        <StepLabel>{label}</StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                        </Grid>
                    </>
                )}
                <Grid item xs={12} sm={9}>
                    <Paper className={classes.containerPaper}>
                        <div className={classes.contentDiv} ref={conteudoRef}>
                            {getStepContent(activeStep, isMobile ? currentMobileStep?.sectionId : null)}
                        </div>
                        {!isMobile && (
                        <Grid container spacing={3} className={classes.buttonsGrid}>
                            <Grid item xs={12}>
                                <Divider />
                            </Grid>
                            {isEdit ? (
                                <>
                                    <Grid item xs={12} sm={9}>
                                        <div className={classes.buttonsDiv}>
                                            <Button
                                                className={classes.textButton}
                                                onClick={onCancel}>
                                                Cancelar
                                            </Button>
                                        </div>
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <Button
                                            variant="contained"
                                            onClick={onSaveEdit}
                                            disabled={enviando || somenteConsulta}
                                            startIcon={<Save />}
                                            className={classes.containedButton}>
                                            Salvar
                                        </Button>
                                    </Grid>
                                </>
                            ) : (
                                <>
                                    <Grid item xs={12} sm={9}>
                                        <div className={classes.buttonsDiv}>
                                            <Button
                                                className={classes.textButton}
                                                onClick={onCancel}>
                                                Cancelar
                                            </Button>
                                            <Button
                                                onClick={onBack}
                                                className={clsx(classes.textButton, classes.voltarButton)}
                                                disabled={activeStep === 0}>
                                                Voltar
                                            </Button>
                                        </div>
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        {activeStep === STEP_LABELS.length - 1 ? (
                                            <Button
                                                variant="contained"
                                                startIcon={<DescriptionOutlined />}
                                                className={classes.containedButton}
                                                disabled={enviando}
                                                onClick={onFinish}>
                                                Finalizar Inscrição
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="contained"
                                                onClick={onNext}
                                                startIcon={<ArrowForward />}
                                                className={classes.containedButton}>
                                                Avançar
                                            </Button>
                                        )}
                                    </Grid>
                                </>
                            )}
                        </Grid>
                        )}
                    </Paper>
                </Grid>
                {!isMobile && (
                    <Grid item xs={12} sm={3}>
                        <BoxResumoInscricao inscricaoFallback={inscricao} />
                    </Grid>
                )}
            </Grid>
            <FloatingBottomBar mobileOnly alwaysRender autoHide={false} aria-label="Navegação da inscrição">
                <MobileRegistrationNavigation
                    mobileSteps={mobileSteps}
                    mobileIndex={mobileIndex}
                    maxLiberado={maxLiberado}
                    isEdit={isEdit}
                    enviando={enviando}
                    salvarBloqueado={somenteConsulta}
                    onBack={onBack}
                    onCancel={onCancel}
                    onFinish={onFinish}
                    onNext={onNext}
                    onSaveEdit={onSaveEdit}
                    onStepChange={onSelecionarEtapa}
                />
            </FloatingBottomBar>
        </div>
    );
}
